import React, {useEffect} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";

// Public Pages
import Home from "./pages/Home";
import Skills from "./components/home-components/Skills";
import ContactUs from "./pages/ContactUs";
import News from "./pages/News";
import Education from "./pages/Education";
import BlogDetail from "./pages/BlogDetail";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Library from "./pages/Library";
import Achievements from "./pages/Achievements";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import LearnPath from "./pages/LearnPath";
import Course from "./pages/Course";
import CourseRead from "./pages/CourseRead";
import StudentCoursePlayer from "./pages/StudentCoursePlayer";
import Cart from "./pages/Cart";
import MyCourses from "./pages/MyCourses";
import AllCourses from "./pages/AllCourses";
import MaterialDetail from "./pages/MaterialDetail";
import Books from "./pages/Books";
import Journals from "./pages/Journals";
import LearnerNews from "./pages/LearnerNews";
import LearnerResources from "./pages/LearnerResources";
import LearnerTransactions from "./pages/LearnerTransactions";
import VerifyOtp from "./pages/VerifyOtp";
import PaymentSuccessPage from "./pages/paymentSuccessPage";
import ResourcePerson from "./pages/ResourcePerson"
import TutorRegistration from "./pages/TutorRegistration"
import ResourcePersonLogin from "./resourcePersonPages/ResourcePersonLogin.jsx"
import CartModal from "./components/CartModal";
import UserFeedback from "./components/UserFeedback";
import { Toaster } from "./components/ui/toaster";
import GoogleOneTapPublicPrompt from "./components/GoogleOneTapPublicPrompt";
import RequireAuth from "./components/RequireAuth";
import AppErrorBoundary from "./components/AppErrorBoundary";

// Admin Pages
import AdminLogin from "./adminPages/AdminLogin";
import AdminDashboard from "./adminPages/Dashboard";
import UserManagement from "./adminPages/UserManagement";
import StudentCoursesPage from "./adminPages/StudentCoursesPage";
import ResourcePersonManagement from "./adminPages/ResourcePersonManagement";
import ResourcePersonEarningsPage from "./adminPages/ResourcePersonEarningsPage";
import ResourcePersonResourcesPage from "./adminPages/ResourcePersonResourcesPage";
import ResourcePersonStudentsPage from "./adminPages/ResourcePersonStudentsPage";
import StaffManagement from "./adminPages/StaffManagement";
import StaffDetailPage from "./adminPages/StaffDetailPage";
import StaffActivityPage from "./adminPages/StaffActivityPage";
import ActivityLogs from "./adminPages/ActivityLogs";
import ActivityLogDetail from "./adminPages/ActivityLogDetail";
import ExamPins from "./adminPages/ExamPins";
import ExamPinPrice from "./adminPages/ExamPinPrice";
import AdminExamPurchases from "./adminPages/ExamPurchases";
import InvoiceManagement from "./adminPages/InvoiceManagement";
import AcademicResources from "./adminPages/AcademicResources";
import AcademicResourceDetail from "./adminPages/AcademicResourceDetail";
import AcademicResourceEdit from "./adminPages/AcademicResourceEdit";
import LearningPaths from "./adminPages/LearningPaths";
import LearningPathDetail from "./adminPages/LearningPathDetail";
import Notifications from "./adminPages/Notifications";
import MarketplaceManagement from "./adminPages/MarketplaceManagement";
import Reports from "./adminPages/Reports";
import WithdrawalRequests from "./adminPages/WithdrawalRequests";
import AdminNewsManagement from "./adminPages/NewsManagement";
import AdminNewsDetail from "./adminPages/NewsDetail";
import AdminNewsEditor from "./adminPages/NewsEditor";
import Categories from "./adminPages/Categories";
import Departments from "./adminPages/Departments";
import Settings from "./adminPages/Settings";
import Reconciliation from "./adminPages/Reconciliation";
import StaffDashboard from "./staffPages/StaffDashboard";
import NewsManagement from "./staffPages/NewsManagement";
import StaffNewsDetail from "./staffPages/StaffNewsDetail";
import StaffContentReview from "./staffPages/StaffContentReview";
import StaffCategories from "./staffPages/StaffCategories";
import StaffDepartments from "./staffPages/StaffDepartments";
import StaffMarketplace from "./staffPages/StaffMarketplace";
import StaffNotifications from "./staffPages/StaffNotifications";
import StaffExamPins from "./staffPages/ExamPins";
import StaffExamPurchases from "./staffPages/ExamPurchases";
import StaffEmails from "./staffPages/Emails";
import StaffLogin from "./staffPages/StaffLogin";
import StaffResetPassword from "./staffPages/StaffResetPassword";
import StaffProfile from "./staffPages/StaffProfile";

// Resource Person Pages
import ResourcePersonDashboard from "./resourcePersonPages/Dashboard";
import ResourcePersonLibrary from "./resourcePersonPages/Library";
import AssignmentsPage from "./resourcePersonPages/AssignmentsPage";
import ReportsPage from "./resourcePersonPages/ReportsPage";
import QuizzesPage from "./resourcePersonPages/QuizzesPage";
import StudentProgressPage from "./resourcePersonPages/StudentProgressPage";
import ChatPage from "./resourcePersonPages/ChatPage";
import ResourcePersonProfile from "./resourcePersonPages/Profile";
import ResourcePersonCourseBuilder from "./resourcePersonPages/ResourcePersonCourseBuilder";
import ResourcePersonPreview from "./resourcePersonPages/ResourcePersonPreview";
import ResourcePersonLearningPathBuilder from "./resourcePersonPages/ResourcePersonLearningPathBuilder";
import ResourcePersonSubmissions from "./resourcePersonPages/ResourcePersonSubmissions";
import ResourcePersonAssessment from "./resourcePersonPages/Assessment";
import SubscriptionsPage from "./resourcePersonPages/Subscriptions";
import ResourcePersonWithdrawals from "./resourcePersonPages/Withdrawals";
import BookUpload from "./resourcePersonPages/BookUpload";
import ResourcePersonMarketplace from "./resourcePersonPages/ResourcePersonMarketplace";
import ResourcePersonMarketplaceCreate from "./resourcePersonPages/ResourcePersonMarketplaceCreate";
import ResourcePersonNotifications from "./resourcePersonPages/Notifications";
import JournalsPage from "./resourcePersonPages/Journals";
import BankDetails from "./resourcePersonPages/BankDetails";

// Layouts
import ResourcePersonLayout from "./layouts/ResourcePersonLayout";
import Assessment from "./resourcePersonPages/Assessment";
import SupportPage from "./resourcePersonPages/SupportPage";

/**
 * Session handling lives in the route guard (`RequireAuth`), which wraps every
 * authenticated area. Public pages never ask for a session, so the refresh
 * endpoint is only ever called from a protected route.
 */
const CourseBuilderRedirect = () => {
  const {id, pathId} = useParams();
  const returnToPath = pathId ? `?pathId=${encodeURIComponent(pathId)}` : "";
  return <Navigate to={`/resource-person/content-library/course/${id}/build${returnToPath}`} replace />;
};

/** Legacy detail URLs redirect to their public marketplace resource pages. */
const PublicBookDetailRedirect = ({type}) => {
  const {id} = useParams();
  return <Navigate to={`/materials/${type}/${encodeURIComponent(id)}`} replace />;
};

/** Every route/query change starts at the top of the new page. */
function ScrollToTop() {
  const {pathname, search} = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
  return null;
}

/** Shared shell for every admin page: consistent type scale + overflow guards. */
function AdminLayout() {
  return (
    <div className="admin-shell min-h-screen w-full overflow-x-clip">
      <Outlet />
    </div>
  );
}

function App() {
  return (
    <Router basename="/">
      <ScrollToTop />
      <CartModal />
      <UserFeedback />
      <Toaster />
      {/* One Tap popup — public pages only. Never the auth entry pages */}
      <GoogleOneTapPublicPrompt />
      <AppErrorBoundary>
      <Routes>
        {/* Public Routes (no sidebar) */}
        <Route path="/" element={<Home />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/skills" element={<Skills />} />
        <Route path="/blog" element={<News />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/education" element={<Education />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/books" element={<Books />} />
        <Route path="/books/:id" element={<PublicBookDetailRedirect type="books" />} />
        <Route path="/journals" element={<Journals />} />
        <Route path="/journals/:id" element={<PublicBookDetailRedirect type="journals" />} />
        <Route path="/materials/:type/:idOrSlug" element={<MaterialDetail />} />
        <Route path="/courses/:id" element={<Course />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/resource-person-registration" element={<TutorRegistration />} />
        <Route path="/resource-personlogin" element={<ResourcePersonLogin />} />
        <Route path="/verify-email" element={<VerifyOtp />} />
        {/* Public instructions page for resource persons (exact path only) */}
        <Route path="/resource-person" element={<ResourcePerson />} />

        {/* ── Learner area — protected: the session is restored before render ── */}
        <Route element={<RequireAuth role="user" policy="soft" />}>
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/content-library" element={<Library />} />
          <Route path="/content-library/path/:id" element={<LearnPath />} />
          <Route path="/courses/:id" element={<Course />} />
          <Route path="/content-library/path/:pathId/read" element={<CourseRead />} />
          <Route path="/courses/:id/read" element={<StudentCoursePlayer />} />
          {/* Learner pages — every dropdown item lands on a real, wired page */}
          <Route path="/my-courses" element={<MyCourses />} />
          {/* GET /api/learners/courses — browse + filter by category */}
          <Route path="/courses" element={<AllCourses />} />
          <Route path="/learner/news" element={<LearnerNews />} />
          <Route path="/learner/resources" element={<LearnerResources />} />
          <Route path="/learner/transactions" element={<LearnerTransactions />} />
          <Route path="/achievements" element={<Achievements />} />
          <Route path="/account/settings" element={<Profile />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment/callback" element={<PaymentSuccessPage />} />
        </Route>

        {/* ── Resource person area (sidebar layout) — protected ── */}
        <Route element={<RequireAuth role="resource-person" policy="soft" />}>
        <Route path="/resource-person" element={<ResourcePersonLayout />}>
          <Route path="dashboard" element={<ResourcePersonDashboard />} />
          <Route path="assessment" element={<Assessment />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="notifications" element={<ResourcePersonNotifications />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="assessment/assignments" element={<AssignmentsPage />} />
          <Route path="assessment/reports" element={<ReportsPage />} />
          <Route path="assessment/quizzes" element={<QuizzesPage />} />
          <Route path="assessment/students" element={<StudentProgressPage />} />
          <Route path="content-library" element={<ResourcePersonLibrary />} />
          <Route path="account/settings" element={<ResourcePersonProfile />} />
          {/* Spec-wired builders: Stages 0–2 (course), bundles (paths), Stage 7 (grading).
              `/course/new` MUST precede `/course/:id` so the literal wins. */}
          <Route path="content-library/course/new" element={<ResourcePersonCourseBuilder />} />
          <Route path="content-library/course/:id/build" element={<ResourcePersonCourseBuilder />} />
          <Route path="content-library/path/:pathId/course/new" element={<ResourcePersonCourseBuilder />} />
          <Route path="content-library/path/:pathId/course/:id" element={<CourseBuilderRedirect />} />
          {/* Read-only "what the learner will see" view. Declared after /build so
              the more specific segment keeps winning. */}
          <Route path="content-library/course/:id/preview" element={<ResourcePersonPreview />} />
          <Route path="content-library/course/:id/submissions" element={<ResourcePersonSubmissions />} />
          <Route path="content-library/path/new" element={<ResourcePersonLearningPathBuilder />} />
          <Route path="content-library/path/:id/build" element={<ResourcePersonLearningPathBuilder />} />
          <Route path="content-library/course/:id" element={<CourseBuilderRedirect />} />
          {/* Learning-path detail reuses the Stage-4 builder (spec endpoints). /build declared
              above so the literal segment still wins over `:id`. */}
          <Route path="content-library/path/:id" element={<ResourcePersonLearningPathBuilder />} />
          {/* Legacy creator pointed at non-spec endpoints — redirect to the Stage 1 builder. */}
          <Route path="content-library/create" element={<Navigate to="/resource-person/content-library/course/new" replace />} />
          <Route path="assessment" element={<ResourcePersonAssessment />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="withdrawals" element={<ResourcePersonWithdrawals />} />
          <Route path="journals" element={<JournalsPage />} />
          <Route path="journals/:id" element={<JournalsPage />} />
          <Route path="bank-details" element={<BankDetails />} />
          <Route path="book-upload" element={<BookUpload />} />
          <Route path="book-upload/:id" element={<BookUpload />} />
          <Route path="marketplace" element={<ResourcePersonMarketplace />} />
          <Route path="marketplace/new" element={<ResourcePersonMarketplaceCreate />} />
          <Route
            path="project-topic"
            element={<Navigate to="/resource-person/marketplace?type=project-topics" replace />}
          />
          <Route
            path="seminar-topic"
            element={<Navigate to="/resource-person/marketplace?type=seminar-topics" replace />}
          />
          {/* Legacy authoring URLs reuse the Postman-aligned course builder. */}
          <Route path="content-library/course/:id/module/create" element={<CourseBuilderRedirect />} />
          <Route path="content-library/course/:id/module/edit/:moduleId" element={<CourseBuilderRedirect />} />
          <Route path="content-library/course/module/:id/assessment" element={<Navigate to="/resource-person/content-library" replace />} />
        </Route>
        </Route>

        {/* ── Admin area — protected ── */}
        <Route path="/adminlogin" element={<AdminLogin />} />
        <Route element={<RequireAuth role="admin" policy="strict" />}>
        <Route element={<AdminLayout />}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/user-management" element={<UserManagement />} />
        <Route path="/admin/user-management/:studentId/courses" element={<StudentCoursesPage />} />
        <Route path="/admin/academic-resources" element={<AcademicResources />} />
        <Route path="/admin/resources/:kind/:resourceId" element={<AcademicResourceDetail />} />
        <Route path="/admin/resources/:kind/:resourceId/edit" element={<AcademicResourceEdit />} />
        <Route path="/admin/learning-paths" element={<LearningPaths />} />
        <Route path="/admin/learning-paths/:learningPathId" element={<LearningPathDetail />} />
        <Route path="/admin/notifications" element={<Notifications />} />
        {/* Resource persons use /api/admin/resource-persons */}
        <Route path="/admin/resource-persons" element={<ResourcePersonManagement />} />
        <Route path="/admin/resource-persons/:id/earnings" element={<ResourcePersonEarningsPage />} />
        <Route path="/admin/resource-persons/:id/resources" element={<ResourcePersonResourcesPage />} />
        <Route path="/admin/resource-persons/:id/students" element={<ResourcePersonStudentsPage />} />
        {/* Staff accounts use /api/admin/staff */}
        <Route path="/admin/staff-management" element={<StaffManagement />} />
        <Route path="/admin/staff-management/:staffId/activity" element={<StaffActivityPage />} />
        <Route path="/admin/staff-management/:staffId" element={<StaffDetailPage />} />
        <Route path="/admin/categories" element={<Categories />} />
        <Route path="/admin/departments" element={<Departments />} />
        <Route path="/admin/settings" element={<Settings />} />
        <Route path="/admin/reconciliation" element={<Reconciliation />} />
        <Route path="/admin/invoice-management" element={<InvoiceManagement />} />
        <Route path="/admin/marketplace-management" element={<MarketplaceManagement />} />
        <Route path="/admin/reports" element={<Reports />} />
        <Route path="/admin/withdrawal-requests" element={<WithdrawalRequests />} />
        <Route path="/admin/news-management" element={<AdminNewsManagement />} />
        <Route path="/admin/news/create" element={<AdminNewsEditor />} />
        <Route path="/admin/news/:newsId/edit" element={<AdminNewsEditor />} />
        <Route path="/admin/news/:newsId" element={<AdminNewsDetail />} />
        <Route path="/admin/activity-logs" element={<ActivityLogs />} />
        <Route path="/admin/activity-logs/:logId" element={<ActivityLogDetail />} />
        <Route path="/admin/exam-pins" element={<ExamPins />} />
        <Route path="/admin/exam-pins/price" element={<ExamPinPrice />} />
                <Route path="/admin/exam-pins/:pinId" element={<ExamPins />} />
        <Route path="/admin/exam-purchases" element={<AdminExamPurchases />} />
        </Route>
        </Route>

        {/* ── Staff area — protected ── */}
        <Route element={<RequireAuth role="staff" policy="strict" />}>
        <Route path="/staff/dashboard" element={<StaffDashboard />} />
        <Route path="/staff/news-management" element={<NewsManagement />} />
        <Route path="/staff/news/:newsId" element={<StaffNewsDetail />} />
        <Route path="/staff/content-review" element={<StaffContentReview />} />
        <Route path="/staff/categories" element={<StaffCategories />} />
        <Route path="/staff/departments" element={<StaffDepartments />} />
        <Route path="/staff/marketplace" element={<StaffMarketplace />} />
        <Route path="/staff/notifications" element={<StaffNotifications />} />
        <Route path="/staff/exam-pins" element={<StaffExamPins />} />
        <Route path="/staff/exam-purchases" element={<StaffExamPurchases />} />
        <Route path="/staff/emails" element={<StaffEmails />} />
        <Route path="/staff/account/settings" element={<StaffProfile />} />
        </Route>

        {/* ── Public staff auth pages ── */}
        <Route path="/staff/login" element={<StaffLogin />} />
        <Route path="/staff/reset-password" element={<StaffResetPassword />} />
      </Routes>
      </AppErrorBoundary>
    </Router>
  );
}

export default App;
